import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * The one hairline divider primitive (design addendum 2026-09-21:
 * "standardize ... dividers" / "ONE divider system"). Every separator line
 * in the app - `SectionLabel`'s own rule, `Card` footers, `Table` rows,
 * `DropdownMenu`/`Select` separators, `Dialog`/`Popover` section edges, the
 * sidebar's group rule - draws from this same `--color-line` hairline at one
 * alpha. Never reach for `<hr>`, a Tailwind palette border
 * (`border-gray-*`), a double-width border (`border-t-2`), or a one-off
 * `rgba(...)` line; use this instead, or the bare `border-line`/`bg-line`
 * utilities this wraps.
 *
 * Content separation should usually prefer whitespace + `SectionLabel` over
 * a drawn line at all (addendum) - reach for `Divider` where content would
 * otherwise visually merge (a row inside a list, a footer under a card),
 * not as a default section break.
 *
 * @param {Object} props
 * @param {'horizontal'|'vertical'} [props.orientation='horizontal']
 * @param {boolean} [props.inset=false] - Pulls the line in from both ends
 *   (16px) instead of running edge-to-edge, for use inside a padded
 *   container (a menu, a card) where the parent isn't already providing
 *   that gutter.
 * @param {React.ReactNode} [props.label] - Centers a `SectionLabel`-styled
 *   caption on the line (an "or" break, a date divider) instead of a bare
 *   rule. Only meaningful with `orientation="horizontal"`.
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const Divider = React.forwardRef(function Divider(
  { orientation = 'horizontal', inset = false, label, className, ...props },
  ref
) {
  if (label) {
    return (
      <div
        ref={ref}
        role="separator"
        aria-orientation="horizontal"
        className={cn('flex items-center gap-3', inset && 'mx-4', className)}
        {...props}
      >
        <span aria-hidden="true" className="h-px min-w-4 flex-1 bg-line" />
        <span className="shrink-0 text-label font-semibold text-ink-2">{label}</span>
        <span aria-hidden="true" className="h-px min-w-4 flex-1 bg-line" />
      </div>
    )
  }

  const isVertical = orientation === 'vertical'

  return (
    <div
      ref={ref}
      role="separator"
      aria-orientation={orientation}
      className={cn(
        isVertical ? 'h-full w-px self-stretch bg-line' : 'h-px w-full bg-line',
        inset && (isVertical ? 'my-2' : 'mx-4'),
        className
      )}
      {...props}
    />
  )
})
Divider.displayName = 'Divider'

export { Divider }
